import { useMutation, useQuery } from "@tanstack/react-query";

import axiosInstance from "./axios";

export type DeadStockProduct = {
  id: number;
  claimId: number;
  claimNumber: string;
  saleId: number;
  invoiceNumber: string;
  branchId: number;
  purchaseProductId: number;
  quantity: number;
  replacementQuantity: number;
  refundAmount: number;
  isSettled: boolean;
  replacementReceived: boolean;
  reportedAt: string;
  unit?: string;
  conversionToBaseUnit?: number;
};

export type DeadStockProductsResponse = {
  content: DeadStockProduct[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  isLast: boolean;
};

export type DeadStockProductsParams = {
  saleId?: number;
  invoiceNo?: string;
  branchId?: number;
  claimId?: number;
  claimNumber?: string;
  isSettled?: boolean;
  replacementReceived?: boolean;
  fromDate?: string;
  toDate?: string;
  page: number;
  pageSize: number;
};

export type ReceiveReplacementPayload = {
  id: number;
  quantity: number;
  unit?: string;
  conversionToBaseUnit?: number;
  baseQuantity?: number;
};

const cleanParams = (params: DeadStockProductsParams) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== "",
    ),
  );

export const useGetDeadStockProducts = (params: DeadStockProductsParams) => {
  return useQuery({
    queryKey: ["useGetDeadStockProducts", params],
    queryFn: async () => {
      const { data } = await axiosInstance.get<DeadStockProductsResponse>(
        "/warranty/damage-products/all",
        {
          params: cleanParams(params),
        },
      );
      return data;
    },
  });
};

export const useReceiveDeadStockReplacement = () => {
  return useMutation({
    mutationFn: async ({ id, ...payload }: ReceiveReplacementPayload) => {
      const { data } = await axiosInstance.post(
        `/warranty/${id}/receive-replacement`,
        {
          ...payload,
        },
      );
      return data;
    },
  });
};

export type WriteOffDeadStockPayload = {
  writeOffDate: string;
  reason: string;
  notes: string;
  damagedProductIds: number[];
};

export const useWriteOffDeadStock = () => {
  return useMutation({
    mutationFn: async (payload: WriteOffDeadStockPayload) => {
      const { data } = await axiosInstance.post("/write-offs/dead-stock", payload);
      return data;
    },
  });
};
